import React, { useState, useEffect, useContext, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Link,
  Snackbar
} from '@mui/material';
import staticLogo from '../images/dhr-poppins_.png';
import animatedLogo from '../images/output_reduced_240.gif';
import { API_URL } from '../utils/apiConfig';

// Memoized image switching component
const TimedImageSwitch = memo(({ isAnimating }) => (
  <Box
    sx={{
      position: 'relative',
      width: '240px',
      height: '240px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}
  >
    <Box
      component="img"
      src={isAnimating ? animatedLogo : staticLogo}
      alt="DaileyHR Logo"
      sx={{
        width: '240px',
        height: 'auto',
        objectFit: 'contain',
        transition: 'opacity 0.5s ease-in-out'
      }}
    />
  </Box>
));

// Memoized countdown display component
const CountdownDisplay = memo(({ timeLeft }) => (
  <Typography variant="body2" sx={{ marginBottom: 2 }}>
    Enter the token sent to your email ({timeLeft} seconds remaining)
  </Typography>
));

CountdownDisplay.displayName = 'CountdownDisplay';

const Login = () => {
  const { login, isAuthenticated, userRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [step, setStep] = useState('email');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [useNewLogin, setUseNewLogin] = useState(
    localStorage.getItem('useNewLogin') === 'true' || 
    (process.env.NODE_ENV === 'production' && localStorage.getItem('useNewLogin') !== 'false')
  );
  
  // If already authenticated, redirect
  useEffect(() => {
    console.log('Login component - Auth state:', { isAuthenticated, userRole });
    
    if (isAuthenticated) {
      console.log('Already authenticated, redirecting to proper page');
      
      // Force a new URL navigation rather than using React Router
      // This ensures a full page refresh and proper navigation
      const redirectPath = (userRole === 'admin' || userRole === 'manager') 
        ? '/dashboard' 
        : '/employee_forms';
      
      console.log(`Forcing redirect to ${redirectPath}`);
      window.location.href = redirectPath;
    }
  }, [isAuthenticated, userRole, navigate]);

  // Save login preference
  useEffect(() => {
    localStorage.setItem('useNewLogin', useNewLogin);
  }, [useNewLogin]);

  // Check for session expiration
  useEffect(() => {
    const sessionExpired = localStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      setSessionExpiredOpen(true);
      // Clear the flag so it doesn't show again on page refresh
      localStorage.removeItem('sessionExpired');
    }
  }, []);

  // Timer effect for token expiration
  useEffect(() => {
    let timer;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setStep('email');
            setError('Token expired. Please request a new one.');
            setIsAnimating(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Helper function to safely decode JWT
  const decodeToken = (jwtToken) => {
    try {
      const base64Url = jwtToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => 
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        ).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (err) {
      console.error('Error decoding token:', err);
      return {};
    }
  };

  // Helper function to safely store employee name
  const setEmployeeName = (employee) => {
    if (!employee) return null;
    
    const firstName = employee.firstName || '';
    const lastName = employee.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    if (fullName && fullName !== ' ') {
      localStorage.setItem('employeeName', fullName);
      return fullName;
    }
    
    return null;
  };

  const checkWebAuthnStatus = async (email) => {
    try {
      const response = await fetch(`${API_URL}/auth/webauthn/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Failed to check WebAuthn status');
      }

      const data = await response.json();
      
      // User has WebAuthn configured
      if (data.isRegistered) {
        // Check if they need email re-verification (30-day cycle)
        if (data.needsEmailVerification) {
          console.log('Email re-verification needed (30-day cycle or never verified)');
          // They need to do email verification first, then will use WebAuthn
          return false;
        }
        
        // They can use WebAuthn directly
        console.log('User can use WebAuthn directly');
        navigate('/webauthn-auth', { 
          state: { 
            email,
            isInitialSetup: false
          }
        });
        return true;
      }
      
      // User doesn't have WebAuthn set up
      console.log('User does not have WebAuthn configured');
      
      return false;
    } catch (error) {
      console.error('Error checking WebAuthn status:', error);
      return false;
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // First check if we can proceed directly to WebAuthn
      const canUseWebAuthn = await checkWebAuthnStatus(email);
      if (canUseWebAuthn) {
        return; // Navigation already handled in checkWebAuthnStatus
      }

      // Otherwise, request email token
      const response = await fetch(`${API_URL}/auth/request-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send token');
      }

      setStep('token');
      setTimeLeft(120);
      setIsAnimating(true);
    } catch (err) {
      setError(err.message || 'Failed to process request');
      setIsAnimating(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTokenVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
  
    try {
      // Include current timestamp to update last_email_verification in the database
      const response = await fetch(`${API_URL}/auth/verify-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email, 
          token,
          verificationTime: new Date().toISOString() 
        }),
      });
  
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Invalid token");
  
      // Check if WebAuthn setup is required or needs renewal
      if (data.requiresWebAuthn || data.webauthnExpired || data.lastEmailVerificationExpired) {
        console.log(
          data.webauthnExpired 
            ? 'WebAuthn token expired, renewal required' 
            : data.lastEmailVerificationExpired
              ? 'Email verification expired, renewal required'
              : 'WebAuthn setup required'
        );
        navigate('/webauthn-auth', { 
          state: { 
            email,
            isInitialSetup: true,
            isRenewal: data.webauthnExpired || data.lastEmailVerificationExpired || false,
            userData: data.user,
            employeeData: data.employee,
            sessionToken: data.token
          }
        });
        return;
      }

      // Decode token to extract employeeId
      const decodedToken = decodeToken(data.token);
      const tokenEmployeeId = decodedToken.employeeId;
      
      // Determine effective employeeId from all possible sources
      const effectiveEmployeeId = tokenEmployeeId || 
                                (data.employee && data.employee.id) || 
                                (data.user && data.user.employeeId);
      
      if (!effectiveEmployeeId) {
        console.warn('No employeeId found in token or response data');
      }

      // Store employee name if available
      let employeeName = null;
      if (data.employee) {
        employeeName = setEmployeeName(data.employee);
      } else if (effectiveEmployeeId) {
        // Try to fetch employee data
        try {
          const employeeResponse = await fetch(`${API_URL}/employees/${effectiveEmployeeId}`, {
            headers: { 'Authorization': `Bearer ${data.token}` }
          });
          
          if (employeeResponse.ok) {
            const employeeData = await employeeResponse.json();
            employeeName = setEmployeeName(employeeData);
          }
        } catch (err) {
          console.warn('Failed to fetch employee data:', err);
        }
      }
  
      // Get employee name, attempting from multiple sources
      let fullName = null;
      
      // Try to get name from employee object
      if (data.employee) {
        if (data.employee.name) {
          fullName = data.employee.name;
        } else if (data.employee.firstName || data.employee.lastName) {
          fullName = `${data.employee.firstName || ''} ${data.employee.lastName || ''}`.trim();
        }
      }
      
      console.log('About to call login with:', {
        token: data.token ? 'Has token' : 'No token',
        role: data.user.role,
        store: String(data.user?.store || ''),
        employeeId: effectiveEmployeeId,
        name: fullName
      });
      
      // ULTRA DIRECT LOGIN - Extreme measures
      try {
        console.warn('⚠️ EMERGENCY OVERRIDE: Implementing direct login with extreme measures');
        
        // 1. Store critical data in localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('userRole', data.user.role);
        localStorage.setItem('employeeId', effectiveEmployeeId || '');
        localStorage.setItem('isAuthenticated', 'true'); // Explicit authentication flag
        
        // 2. Determine where to go based on role
        const redirectPath = (data.user.role === 'admin' || data.user.role === 'manager')
          ? '/dashboard'
          : '/employee_forms';
          
        // TRIPLE APPROACH - Try three different methods to ensure redirection works
        
        // Method 1: Set location directly
        window.location.href = redirectPath;
        
        // Method 2: Set location with base path (fallback)
        setTimeout(() => {
          window.location.href = window.location.origin + redirectPath;
        }, 50);
        
        // Method 3: Use replace (another fallback)
        setTimeout(() => {
          window.location.replace(redirectPath);
        }, 100);
        
        return; // Stop here to prioritize redirection
      } catch (redirectErr) {
        console.error('CRITICAL: Redirection failed with error:', redirectErr);
      }
    } catch (err) {
      setError(err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // Legacy login handler
  const handleLegacyLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
  
    try {
      console.log('Attempting legacy login...');
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.error || "Invalid credentials");
      }
  
      const { token, user, employee } = data;
  
      if (!token || !user) {
        throw new Error("Invalid server response - missing required data");
      }
  
      // Decode token to verify employeeId
      const decodedToken = decodeToken(token);
      console.log('Decoded token:', decodedToken);
      
      // Determine effective employeeId from all possible sources
      const tokenEmployeeId = decodedToken.employeeId;
      const effectiveEmployeeId = tokenEmployeeId || 
                                (employee && employee.id) || 
                                user.employeeId;
      
      if (!effectiveEmployeeId) {
        console.warn('No employeeId found in token or response data');
      }
  
      // Handle employee name
      let employeeName = null;
      if (employee) {
        employeeName = setEmployeeName(employee);
      } else if (effectiveEmployeeId) {
        // Try to fetch employee data
        try {
          const employeeResponse = await fetch(`${API_URL}/employees/${effectiveEmployeeId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (employeeResponse.ok) {
            const employeeData = await employeeResponse.json();
            employeeName = setEmployeeName(employeeData);
          }
        } catch (err) {
          console.warn('Failed to fetch employee data:', err);
        }
      }
  
      console.log('Login data prepared:', {
        role: user.role,
        store: employee?.locationId || user?.store,
        employeeId: effectiveEmployeeId,
        employeeName
      });
  
      // Get employee name
      let fullName = null;
      
      // Try to get name from employee object
      if (employee) {
        if (employee.name) {
          fullName = employee.name;
        } else if (employee.firstName || employee.lastName) {
          fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
        }
      }
      
      console.log('Legacy login about to call login with:', {
        token: token ? 'Has token' : 'No token',
        role: user.role,
        store: employee?.locationId || user?.store,
        employeeId: effectiveEmployeeId,
        name: fullName
      });
      
      // ULTRA DIRECT LOGIN - Extreme measures for legacy login
      try {
        console.warn('⚠️ EMERGENCY OVERRIDE: Implementing direct legacy login with extreme measures');
        
        // 1. Store critical data in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('userRole', user.role);
        localStorage.setItem('employeeId', effectiveEmployeeId || '');
        localStorage.setItem('isAuthenticated', 'true'); // Explicit authentication flag
        if (fullName) {
          localStorage.setItem('employeeName', fullName);
        }
        
        // 2. Determine where to go based on role
        const redirectPath = (user.role === 'admin' || user.role === 'manager')
          ? '/dashboard'
          : '/employee_forms';
          
        // TRIPLE APPROACH - Try three different methods to ensure redirection works
        
        // Method 1: Set location directly
        window.location.href = redirectPath;
        
        // Method 2: Set location with base path (fallback)
        setTimeout(() => {
          window.location.href = window.location.origin + redirectPath;
        }, 50);
        
        // Method 3: Use replace (another fallback)
        setTimeout(() => {
          window.location.replace(redirectPath);
        }, 100);
        
        return; // Stop here to prioritize redirection
      } catch (redirectErr) {
        console.error('CRITICAL: Legacy login redirection failed with error:', redirectErr);
      }
    } catch (err) {
      console.error("Error during login:", err);
      setError(err.message || "Login failed. Please check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9f9f9',
        padding: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          padding: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <Box sx={{ marginBottom: 3 }}>
          <Box sx={{ marginBottom: 2, marginTop: -2, display: 'flex', justifyContent: 'center' }}>
            <TimedImageSwitch isAnimating={isAnimating} />
          </Box>
          <Typography variant="h5" fontWeight="bold">
            DAILEY HR
          </Typography>
        </Box>

        {(process.env.REACT_APP_NODE_ENV === 'development' || process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || process.env.REACT_APP_SHOW_LOGIN_TOGGLE === 'true') && (
          <FormControlLabel
            control={
              <Switch
                checked={useNewLogin}
                onChange={(e) => setUseNewLogin(e.target.checked)}
                name="loginSystem"
              />
            }
            label="Use New Login System"
            sx={{ marginBottom: 2 }}
          />
        )}

        <Box sx={{ minHeight: '200px', position: 'relative' }}>
          {useNewLogin ? (
            // New Login System
            step === 'email' ? (
              <form onSubmit={handleEmailSubmit}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                  margin="normal"
                  autoComplete="email"
                />
                {error && (
                  <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {error}
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ marginTop: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Continue'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleTokenVerify}>
                <CountdownDisplay timeLeft={timeLeft} />
                <TextField
                  label="Token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  fullWidth
                  required
                  margin="normal"
                  autoComplete="off"
                  autoFocus
                />
                {error && (
                  <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {error}
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ marginTop: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verify Token'}
                </Button>
                <Button
                  onClick={() => {
                    setStep('email');
                    setError(null);
                    setToken('');
                    setIsAnimating(false);
                  }}
                  color="secondary"
                  fullWidth
                  sx={{ marginTop: 1 }}
                >
                  Back to Email
                </Button>
              </form>
            )
          ) : (
            // Legacy Login System
            <form onSubmit={handleLegacyLogin}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
              />
              {error && (
                <Alert severity="error" sx={{ marginBottom: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ marginTop: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
            </form>
          )}
        </Box>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Need help? Contact support at{" "}
          <Link href="mailto:support@dailey.llc">support@dailey.llc</Link><br /> or call {" "}
          <Link href="tel:+18555234539">1-855-5-DAILEY</Link>.
        </Typography>
      </Paper>
      
      {/* Session expired notification */}
      <Snackbar
        open={sessionExpiredOpen}
        autoHideDuration={6000}
        onClose={() => setSessionExpiredOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSessionExpiredOpen(false)} 
          severity="warning" 
          sx={{ width: '100%' }}
        >
          Your session has expired. Please log in again.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
